import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Loader2, Plus, Trash2, Edit3, Eye, Send, FileDown, CheckCircle2,
  AlertTriangle, Search, Receipt, Copy, RefreshCw, ArrowRightLeft,
  Repeat, ThumbsUp, ThumbsDown, BellRing, ShieldAlert, Zap
} from "lucide-react";
import { Link } from "react-router-dom";
import api, { tokenStorage, API } from "../../api";

const STATUS = {
  draft: { label: "Entwurf", color: "#94a3b8" },
  sent: { label: "Gesendet", color: "#1E88E5" },
  paid: { label: "Bezahlt", color: "#22C55E" },
  overdue: { label: "Überfällig", color: "#E63946" },
  reminder_7d: { label: "Erinnerung -7T", color: "#3b82f6" },
  reminder_5d: { label: "Erinnerung -5T", color: "#2563eb" },
  reminder_1d: { label: "Erinnerung -1T", color: "#f59e0b" },
  mahnung_1: { label: "1. Mahnung (+20 CHF)", color: "#ea580c" },
  mahnung_2: { label: "2. Mahnung (+40 CHF)", color: "#dc2626" },
  inkasso: { label: "3. Inkasso (+80 CHF)", color: "#7f1d1d" },
  accepted: { label: "Angenommen", color: "#22C55E" },
  declined: { label: "Abgelehnt", color: "#64748b" },
};

export default function InvoiceList({ kind = "invoice" }) {
  const isOffer = kind === "offer";
  const path = isOffer ? "offers" : "invoices";
  const label = isOffer ? "Offerten" : "Rechnungen";
  const newLabel = isOffer ? "Neue Offerte" : "Neue Rechnung";

  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showOnlyRecurring, setShowOnlyRecurring] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [dunningRunning, setDunningRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([api.get(`/admin/${path}`), api.get("/admin/companies")]);
      setItems(r.data); setCompanies(c.data);
    } finally { setLoading(false); }
  }, [path]);

  useEffect(() => { load(); }, [load]);

  const compName = useMemo(() => Object.fromEntries(companies.map((c) => [c.id, c.name])), [companies]);

  const filtered = items.filter((i) => {
    if (statusFilter && i.status !== statusFilter) return false;
    if (showOnlyRecurring && !i.recurring) return false;
    const ql = q.trim().toLowerCase();
    if (!ql) return true;
    return (i.number || "").toLowerCase().includes(ql) || (i.clientName || "").toLowerCase().includes(ql) || (i.category || "").toLowerCase().includes(ql);
  });

  const remove = async (id) => {
    if (!window.confirm("Wirklich löschen?")) return;
    await api.delete(`/admin/${path}/${id}`);
    setItems((arr) => arr.filter((x) => x.id !== id));
  };

  const openPreview = (id) => {
    const token = tokenStorage.get();
    const url = `${API}/admin/${path}/${id}/preview`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.text())
      .then((html) => {
        const w = window.open("", "_blank");
        if (w) { w.document.open(); w.document.write(html); w.document.close(); }
      })
      .catch((e) => alert("Vorschau-Fehler: " + e.message));
  };

  const downloadPDF = async (id, num) => {
    const token = tokenStorage.get();
    const url = `${API}/admin/${path}/${id}/pdf`;
    try {
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) {
        let msg = `HTTP ${r.status}`; try { msg = (await r.json()).detail || msg; } catch (e) { /* ignore */ }
        throw new Error(msg);
      }
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${label}-${num || id}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (e) { alert("Download fehlgeschlagen: " + e.message); }
  };

  const sendDoc = async (it) => {
    if (!it.clientEmail) { alert("Keine E-Mail hinterlegt."); return; }
    const company = companies.find((c) => c.id === it.companyId) || companies.find((c) => c.isDefault) || companies[0];
    const companyInfo = company ? `über Absender: "${company.name}" <${company.email}>` : "";
    if (!window.confirm(`${label} #${it.number || it.id} an ${it.clientEmail}\n${companyInfo}\nsenden?`)) return;
    setBusyId(it.id);
    try {
      const r = await api.post(`/admin/${path}/${it.id}/send`, { toEmail: it.clientEmail });
      if (r.data.ok) {
        setFeedback({
          type: "ok",
          text: `Erfolgreich an ${r.data.to} gesendet (Absender: ${r.data.companyName || company?.name || "Firma"} <${r.data.from || company?.email}>)`
        });
        setItems((arr) => arr.map((x) => x.id === it.id ? { ...x, status: "sent" } : x));
      } else setFeedback({ type: "err", text: `Fehler: ${r.data.error}` });
    } catch (e) { setFeedback({ type: "err", text: e.response?.data?.detail || "Fehler" }); }
    finally { setBusyId(null); }
  };

  const duplicate = async (id) => {
    try {
      const r = await api.post(`/admin/${path}/${id}/duplicate`);
      setItems((arr) => [r.data, ...arr]);
      setFeedback({ type: "ok", text: `Duplikat erstellt: ${r.data.number}` });
    } catch (e) { setFeedback({ type: "err", text: e.response?.data?.detail || "Fehler" }); }
  };

  const convertOfferToInvoice = async (id) => {
    if (!window.confirm("Offerte in eine definitive Rechnung umwandeln?")) return;
    try {
      const r = await api.post(`/admin/offers/${id}/convert-to-invoice`);
      setFeedback({ type: "ok", text: `Rechnung ${r.data.number} erstellt` });
    } catch (e) { setFeedback({ type: "err", text: e.response?.data?.detail || "Fehler" }); }
  };

  const markPaid = async (id) => {
    if (!window.confirm("Rechnung als bezahlt markieren?")) return;
    try {
      await api.post(`/admin/invoices/${id}/mark-paid`);
      setItems((arr) => arr.map((x) => x.id === id ? { ...x, status: "paid" } : x));
      setFeedback({ type: "ok", text: "Als bezahlt markiert" });
    } catch (e) { setFeedback({ type: "err", text: e.response?.data?.detail || "Fehler" }); }
  };

  const setStatus = async (id, st, stLabel) => {
    try {
      await api.patch(`/admin/${path}/${id}/status`, { status: st });
      setItems((arr) => arr.map((x) => x.id === id ? { ...x, status: st } : x));
      setFeedback({ type: "ok", text: `Status auf "${stLabel}" gesetzt` });
    } catch (e) { setFeedback({ type: "err", text: e.response?.data?.detail || "Fehler" }); }
  };

  const runRecurring = async () => {
    try {
      const r = await api.post("/admin/invoices/run-recurring");
      if (r.data.created?.length > 0) {
        setFeedback({ type: "ok", text: `${r.data.created.length} neue Abo-Rechnungen erzeugt: ${r.data.created.join(", ")}` });
        load();
      } else {
        setFeedback({ type: "ok", text: "Keine fälligen Abo-Rechnungen vorhanden." });
      }
    } catch (e) { setFeedback({ type: "err", text: e.response?.data?.detail || "Fehler" }); }
  };

  const runDunningCycle = async () => {
    if (!window.confirm("Mahnwesen- und Erinnerungs-Autopilot jetzt manuell ausführen? Alle fälligen E-Mails und Gebühren werden berechnet.")) return;
    setDunningRunning(true);
    try {
      const r = await api.post("/admin/invoices/run-dunning");
      const s = r.data.stats || {};
      setFeedback({
        type: "ok",
        text: `Mahnlauf erfolgreich: ${s.reminders_7d || 0}x 7T-Erinnerung, ${s.reminders_5d || 0}x 5T-Erinnerung, ${s.reminders_1d || 0}x 1T-Erinnerung, ${s.mahnung_1 || 0}x 1. Mahnung, ${s.mahnung_2 || 0}x 2. Mahnung, ${s.inkasso || 0}x Inkasso.`
      });
      load();
    } catch (e) {
      setFeedback({ type: "err", text: e.response?.data?.detail || "Fehler beim Mahnlauf" });
    } finally {
      setDunningRunning(false);
    }
  };

  return (
    <div data-testid={`${path}-page`} className="space-y-6">
      {/* Header & Controls */}
      <div className="flex items-start justify-between gap-4 flex-wrap pb-2 border-b border-slate-200">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-[#0f172a]">{label}</h1>
          <p className="text-xs text-slate-500 mt-1">
            {items.length} {label.toLowerCase()} • Schweizer QR-Bill & Automatisches 6-Stufen Mahnwesen
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {!isOffer && (
            <>
              <button
                onClick={runDunningCycle}
                disabled={dunningRunning}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-900 border border-amber-300 font-bold text-xs shadow-sm transition disabled:opacity-50"
              >
                {dunningRunning ? <Loader2 size={13} className="animate-spin text-amber-600" /> : <BellRing size={13} className="text-amber-600" />}
                Mahnlauf & Erinnerungen prüfen
              </button>
              <button
                onClick={runRecurring}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs shadow-sm transition"
              >
                <RefreshCw size={13} /> Abos erzeugen
              </button>
            </>
          )}

          <Link
            to={`/admin/${path}/new`}
            data-testid={`new-${path}-btn`}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#E63946] hover:bg-[#c5303d] text-white font-bold text-xs shadow-sm transition"
          >
            <Plus size={14} /> {newLabel}
          </Link>
        </div>
      </div>

      {feedback && (
        <div className={`flex items-start gap-2 text-sm rounded-xl p-3.5 ${feedback.type === "ok" ? "bg-emerald-50 border border-emerald-200 text-emerald-800 font-semibold" : "bg-red-50 border border-red-200 text-red-800 font-semibold"}`}>
          {feedback.type === "ok" ? <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" /> : <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />}
          <span>{feedback.text}</span>
        </div>
      )}

      {/* Filter and Search Bar */}
      <div className="flex gap-2.5 flex-wrap items-center">
        <div className="relative flex-1 max-w-md min-w-[220px]">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechnungsnummer, Kunde oder Kategorie suchen..."
            className="w-full pl-9 pr-3.5 py-2 rounded-xl border border-slate-200 bg-white text-xs focus:border-[#E63946] focus:outline-none shadow-sm"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="status-filter"
          className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-xs font-semibold focus:border-[#E63946] focus:outline-none shadow-sm"
        >
          <option value="">Alle Status</option>
          <option value="draft">Entwurf</option>
          <option value="sent">Gesendet (Offen)</option>
          {!isOffer && (
            <>
              <option value="reminder_7d">Erinnerung 7 Tage vorher</option>
              <option value="reminder_5d">Erinnerung 5 Tage vorher</option>
              <option value="reminder_1d">Erinnerung 1 Tag vorher</option>
              <option value="overdue">Überfällig</option>
              <option value="mahnung_1">1. Mahnung (+CHF 20)</option>
              <option value="mahnung_2">2. Mahnung (+CHF 40)</option>
              <option value="inkasso">3. Stufe Inkasso (+CHF 80)</option>
              <option value="paid">Bezahlt</option>
            </>
          )}
          {isOffer && <option value="accepted">Angenommen</option>}
          {isOffer && <option value="declined">Abgelehnt</option>}
        </select>

        {!isOffer && (
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 px-3 py-2 rounded-xl cursor-pointer shadow-sm">
            <input
              type="checkbox"
              checked={showOnlyRecurring}
              onChange={(e) => setShowOnlyRecurring(e.target.checked)}
              data-testid="recurring-filter"
              className="w-3.5 h-3.5 accent-[#E63946]"
            />
            <span>🔁 Nur Abos</span>
          </label>
        )}

        <span className="text-xs text-slate-500 font-mono ml-auto">
          {filtered.length} / {items.length} Dokumente
        </span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="animate-spin mx-auto text-[#E63946]" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <Receipt size={36} className="mx-auto mb-2 text-slate-300" />
            Noch keine {label.toLowerCase()} vorhanden.
          </div>
        ) : (
          <>
            {/* MOBILE CARDS VIEW (Visible only on mobile screens < 768px) */}
            <div className="block md:hidden divide-y divide-slate-100 p-3 space-y-3">
              {filtered.map((it) => {
                const st = STATUS[it.status] || STATUS.draft;
                return (
                  <div
                    key={it.id}
                    className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono font-black text-sm text-[#0f172a]">
                        {it.number || "—"}
                      </span>
                      <span
                        className="text-[10px] font-extrabold px-2 py-0.5 rounded-md text-white uppercase tracking-wider"
                        style={{ background: st.color }}
                      >
                        {st.label}
                      </span>
                    </div>

                    <div className="text-xs space-y-1">
                      <div className="flex justify-between">
                        <span className="text-slate-400 font-semibold">Kunde:</span>
                        <span className="font-bold text-slate-800 text-right">{it.clientName}</span>
                      </div>
                      {it.clientEmail && (
                        <div className="text-[11px] text-slate-400 text-right truncate">
                          {it.clientEmail}
                        </div>
                      )}
                      <div className="flex justify-between items-baseline pt-1">
                        <span className="text-slate-400 font-semibold">Fällig:</span>
                        <span className="font-mono text-slate-600">
                          {it.dueDate ? new Date(it.dueDate).toLocaleDateString("de-CH") : (it.issueDate || "—")}
                        </span>
                      </div>
                      <div className="flex justify-between items-baseline pt-1">
                        <span className="text-slate-400 font-semibold">Betrag:</span>
                        <div className="text-right">
                          <span className="font-mono font-black text-sm text-slate-900">
                            CHF {Number(it.total || 0).toFixed(2)}
                          </span>
                          {it.dunningFees > 0 && (
                            <span className="block text-[9px] text-red-600 font-bold">
                              +CHF {it.dunningFees.toFixed(2)} Gebühr
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Mobile Action Buttons */}
                    <div className="pt-2 border-t border-slate-100 flex items-center justify-between flex-wrap gap-1.5">
                      <button
                        onClick={() => openPreview(it.id)}
                        title="Vorschau"
                        className="h-8 px-2.5 rounded-lg bg-slate-100 hover:bg-slate-900 hover:text-white text-xs font-bold flex items-center gap-1 transition"
                      >
                        <Eye size={13} />
                        <span>Vorschau</span>
                      </button>

                      <button
                        onClick={() => downloadPDF(it.id, it.number)}
                        title="PDF"
                        className="h-8 px-2.5 rounded-lg bg-slate-100 hover:bg-[#E63946] hover:text-white text-xs font-bold flex items-center gap-1 transition"
                      >
                        <FileDown size={13} />
                        <span>PDF</span>
                      </button>

                      <button
                        onClick={() => sendDoc(it)}
                        disabled={busyId === it.id}
                        title="E-Mail"
                        className="h-8 px-2.5 rounded-lg bg-slate-100 hover:bg-emerald-600 hover:text-white text-xs font-bold flex items-center gap-1 transition disabled:opacity-50"
                      >
                        {busyId === it.id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                        <span>Senden</span>
                      </button>

                      <Link
                        to={`/admin/${path}/${it.id}`}
                        title="Bearbeiten"
                        className="h-8 px-2.5 rounded-lg bg-slate-100 hover:bg-blue-600 hover:text-white text-xs font-bold flex items-center gap-1 transition"
                      >
                        <Edit3 size={13} />
                        <span>Edit</span>
                      </Link>

                      {!isOffer && it.status !== "paid" && (
                        <button
                          onClick={() => markPaid(it.id)}
                          title="Als bezahlt markieren"
                          className="h-8 px-2.5 rounded-lg bg-emerald-50 hover:bg-emerald-600 text-emerald-700 hover:text-white text-xs font-bold flex items-center gap-1 transition"
                        >
                          <CheckCircle2 size={13} />
                          <span>Bezahlt</span>
                        </button>
                      )}

                      <button
                        onClick={() => remove(it.id)}
                        title="Löschen"
                        className="h-8 w-8 rounded-lg bg-slate-100 hover:bg-red-600 hover:text-white flex items-center justify-center transition ml-auto"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* DESKTOP TABLE VIEW (Visible on >= 768px screens) */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-slate-50/80 border-b border-slate-200 text-[11px] font-black uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="p-3">Nr.</th>
                    <th className="p-3">Kunde</th>
                    <th className="p-3 hidden md:table-cell">Kategorie / Firma</th>
                    <th className="p-3 hidden md:table-cell">Fällig</th>
                    <th className="p-3 text-right">Betrag</th>
                    <th className="p-3">Mahnstufe / Status</th>
                    <th className="p-3 text-right">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((it) => {
                    const st = STATUS[it.status] || STATUS.draft;
                    return (
                      <tr key={it.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="p-3 text-xs font-mono font-bold text-slate-900">
                          {it.number || "—"}
                        </td>
                        <td className="p-3">
                          <div className="font-bold text-slate-900 text-xs">{it.clientName}</div>
                          <div className="text-[11px] text-slate-500 truncate max-w-[190px]">{it.clientEmail || ""}</div>
                        </td>
                        <td className="p-3 text-xs hidden md:table-cell">
                          <span className="text-[11px] font-semibold text-slate-700 block">{it.category || "Allgemein"}</span>
                          <span className="text-[10px] text-slate-400">{compName[it.companyId] || "—"}</span>
                        </td>
                        <td className="p-3 text-xs text-slate-600 hidden md:table-cell font-mono">
                          {it.dueDate ? new Date(it.dueDate).toLocaleDateString("de-CH") : (it.issueDate || "—")}
                        </td>
                        <td className="p-3 text-right font-mono font-black text-xs text-slate-900">
                          CHF {Number(it.total || 0).toFixed(2)}
                        </td>
                        <td className="p-3">
                          <span
                            className="text-[10px] font-extrabold px-2.5 py-1 rounded-md text-white inline-block uppercase tracking-wider"
                            style={{ background: st.color }}
                          >
                            {st.label}
                          </span>
                          {it.dunningFees > 0 && (
                            <span className="block text-[9px] text-red-600 font-bold mt-0.5">
                              +CHF {it.dunningFees.toFixed(2)} Gebühr
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-right">
                          <div className="inline-flex gap-1">
                            <button
                              onClick={() => openPreview(it.id)}
                              data-testid={`preview-${it.id}`}
                              title="HTML Vorschau & Druck"
                              className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-900 hover:text-white flex items-center justify-center transition"
                            >
                              <Eye size={13} />
                            </button>
                            <button
                              onClick={() => downloadPDF(it.id, it.number)}
                              data-testid={`pdf-${it.id}`}
                              title="Swiss QR-Bill PDF"
                              className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-[#E63946] hover:text-white flex items-center justify-center transition"
                            >
                              <FileDown size={13} />
                            </button>
                            <button
                              onClick={() => sendDoc(it)}
                              disabled={busyId === it.id}
                              data-testid={`send-${it.id}`}
                              title="Per E-Mail senden"
                              className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-emerald-600 hover:text-white flex items-center justify-center disabled:opacity-50 transition"
                            >
                              {busyId === it.id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                            </button>
                            <Link
                              to={`/admin/${path}/${it.id}`}
                              title="Bearbeiten"
                              className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-blue-600 hover:text-white flex items-center justify-center transition"
                            >
                              <Edit3 size={13} />
                            </Link>
                            <button
                              onClick={() => duplicate(it.id)}
                              title="Duplizieren"
                              data-testid={`dup-${it.id}`}
                              className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-cyan-600 hover:text-white flex items-center justify-center transition"
                            >
                              <Copy size={13} />
                            </button>

                            {isOffer && it.status !== "accepted" && (
                              <button onClick={() => setStatus(it.id, "accepted", "Angenommen")} title="Als angenommen markieren" className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-emerald-600 hover:text-white flex items-center justify-center transition"><ThumbsUp size={13} /></button>
                            )}
                            {isOffer && it.status !== "declined" && (
                              <button onClick={() => setStatus(it.id, "declined", "Abgelehnt")} title="Als abgelehnt markieren" className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-600 hover:text-white flex items-center justify-center transition"><ThumbsDown size={13} /></button>
                            )}
                            {isOffer && (
                              <button onClick={() => convertOfferToInvoice(it.id)} title="In Rechnung umwandeln" className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-emerald-600 hover:text-white flex items-center justify-center transition"><ArrowRightLeft size={13} /></button>
                            )}

                            {!isOffer && it.status !== "paid" && (
                              <button
                                onClick={() => markPaid(it.id)}
                                title="Als bezahlt markieren"
                                className="w-7 h-7 rounded-lg bg-emerald-50 hover:bg-emerald-600 text-emerald-700 hover:text-white flex items-center justify-center transition"
                              >
                                <CheckCircle2 size={13} />
                              </button>
                            )}

                            <button
                              onClick={() => remove(it.id)}
                              title="Löschen"
                              className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-red-600 hover:text-white flex items-center justify-center transition"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
